"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { SearchIcon, X } from "lucide-react";
import { 
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";
import { Button } from "@/components/ui/button";

type SearchResult = {
  id: string;
  title: string;
  type: "property" | "area" | "landlord";
  url: string;
};

// Mock search results - in a real app, this would come from an API
const mockResults: SearchResult[] = [
  { id: "1", title: "123 Main Street", type: "property", url: "/property/123-main-street" },
  { id: "2", title: "456 Oak Avenue", type: "property", url: "/property/456-oak-avenue" },
  { id: "3", title: "Downtown Area", type: "area", url: "/browse-reviews?area=downtown" },
  { id: "4", title: "Uptown District", type: "area", url: "/browse-reviews?area=uptown" },
  { id: "5", title: "John Smith Properties", type: "landlord", url: "/landlord/john-smith" },
];

interface CommandSearchProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CommandSearch({ isOpen, onOpenChange }: CommandSearchProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const router = useRouter();

  // Simulate search functionality
  useEffect(() => {
    if (searchQuery.length > 1) {
      const filteredResults = mockResults.filter(result => 
        result.title.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setResults(filteredResults);
    } else {
      setResults([]);
    }
  }, [searchQuery]);

  const handleSelect = (result: SearchResult) => {
    router.push(result.url);
    onOpenChange(false);
    setSearchQuery("");
  };

  return (
    <CommandDialog open={isOpen} onOpenChange={onOpenChange}>
      <div className="flex items-center border-b px-3">
        <SearchIcon className="mr-2 h-4 w-4 shrink-0 opacity-50" />
        <CommandInput 
          placeholder="Search properties, areas..." 
          value={searchQuery}
          onValueChange={setSearchQuery}
          className="flex h-11 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
        />
        {searchQuery && (
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => setSearchQuery("")}
            className="h-6 w-6"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
      <CommandList>
        {results.length > 0 ? (
          <>
            <CommandGroup heading="Properties">
              {results
                .filter(result => result.type === "property")
                .map(result => (
                  <CommandItem 
                    key={result.id} 
                    onSelect={() => handleSelect(result)}
                  >
                    {result.title}
                  </CommandItem>
                ))}
            </CommandGroup>
            <CommandGroup heading="Areas">
              {results
                .filter(result => result.type === "area")
                .map(result => (
                  <CommandItem 
                    key={result.id} 
                    onSelect={() => handleSelect(result)}
                  >
                    {result.title}
                  </CommandItem>
                ))}
            </CommandGroup>
            <CommandGroup heading="Landlords">
              {results
                .filter(result => result.type === "landlord")
                .map(result => (
                  <CommandItem 
                    key={result.id} 
                    onSelect={() => handleSelect(result)}
                  >
                    {result.title}
                  </CommandItem>
                ))}
            </CommandGroup>
          </>
        ) : (
          <CommandEmpty>No results found.</CommandEmpty>
        )}
      </CommandList>
    </CommandDialog>
  );
}

export default CommandSearch; 